import React from "react";
import { useTheme } from "@mui/material/styles";
import makeStyles from "@mui/styles/makeStyles";
import PropTypes from "prop-types";
import { Paper, Box, Typography } from "@mui/material";
import CourseTypeIcon from "../Icons/CourseTypeIcon";
import CourseCardOverline from "./CourseCardOverline";
import useMediaQuery from "@mui/material/useMediaQuery";
import { courseTypes } from "../../constants";
import Tooltip from "../Tooltip/Tooltip";
import PrerequisiteTooltipContent from "../Tooltip/PrerequisiteTooltipContent";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useTranslation } from "react-i18next";

const useStyles = makeStyles(theme => ({
  root: {
    display: "flex",
    flexDirection: "column",
    marginTop: theme.spacing(3),
    width: "100%"
  },
  image: {
    width: "100%",
    objectFit: "cover",
    height: "100%"
  },
  container: {
    position: "relative",
    height: "100%"
  },
  note: {
    position: "absolute",
    right: "5%",
    top: "10px"
  },
  noteText: {
    verticalAlign: "top",
    fontSize: "1.1rem",
    color: theme.palette.secondary.light
  }
}));

const CourseCardListType = ({
  title,
  buttons,
  activitiesSummary,
  expansionPanel,
  imageSrc,
  hasNote,
  prerequisites,
  courseType,
  typeName,
  flags,
  dueDate,
  reattemptMessage,
  condensed,
  courseDescription,
  number,
  manualName,
  duration,
  isSelfAssigned,
  buttonsVariant
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const smallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const imageSize = smallScreen || condensed ? 222 : 154;
  const { t } = useTranslation();
  let courseTitle =
    courseType == courseTypes.policy ? title + " | " + number : title;

  if (prerequisites && prerequisites.length > 0) {
    courseTitle = (
      <Tooltip
        title={<PrerequisiteTooltipContent prerequisites={prerequisites} />}
        placement="right-end"
      >
        <Typography variant="subtitle1" color="textPrimary">
          {courseTitle}
        </Typography>
      </Tooltip>
    );
  } else {
    courseTitle = (
      <Typography variant="subtitle1" color="textPrimary">
        {courseTitle}
      </Typography>
    );
  }
  const policyManualInfo = courseType == courseTypes.policy && manualName && (
    <Box mb={1} mt={-0.5}>
      <Typography variant="body2" color="textSecondary">
        {t("learningPathListView.manual") + ": " + manualName}
      </Typography>
    </Box>
  );

  const courseDetails = (
    <Box display="flex" flexDirection="column">
      <Box display="flex" flexDirection="row">
        {courseTitle}
        {!condensed && !smallScreen && (
          <Box display="flex" ml={2}>
            {flags}
          </Box>
        )}
      </Box>
      <Box display="flex" flexDirection="column" my={1.5}>
        {policyManualInfo}
        {duration}
        {dueDate}
        {reattemptMessage}
      </Box>
    </Box>
  );

  return (
    <Paper className={classes.root}>
      <Box
        display="flex"
        flexDirection="row"
        zIndex={1}
        alignItems="stretch"
        data-qa="course-card-list-item"
      >
        <Box
          maxWidth={imageSize}
          maxHeight={imageSize}
          minWidth={154}
          minHeight={154}
        >
          <div className={classes.container}>
            {hasNote && (
              <div className={classes.note}>
                <span className={classes.noteText}>
                  {isSelfAssigned
                    ? t("courseCard.courseCardPicture.note.saved")
                    : t("courseCard.courseCardPicture.note.assigned")}
                </span>
                <CheckCircleIcon color="primary" />
              </div>
            )}
            <img src={imageSrc} className={classes.image} alt="Assignment" />
          </div>
        </Box>
        <Box
          display="flex"
          flexDirection="row"
          flex={1}
          mt={{ xs: 3, md: 4 }}
          mx={{ xs: 3, md: 4 }}
          mb={3}
          alignItems="stretch"
        >
          {!condensed && !smallScreen && (
            <Box mr={2} display={{ xs: "none", md: "flex" }}>
              <CourseTypeIcon type={typeName} small="small" />
            </Box>
          )}
          <Box display="flex" flexDirection="column" flex={1} pr={{ md: 3 }}>
            {(condensed || smallScreen) && (
              <Box display="flex" ml={-0.5} mb={0.75}>
                <Box mr={1}>
                  <CourseTypeIcon type={typeName} size="small" />
                </Box>
                {typeName && <CourseCardOverline courseType={typeName} />}
              </Box>
            )}
            <Box display="flex" flex={1}>
              {courseDescription ? (
                <Tooltip
                  placement="top"
                  title={
                    <Typography variant="body2" color="textPrimary">
                      {courseDescription}
                    </Typography>
                  }
                >
                  {courseDetails}
                </Tooltip>
              ) : (
                courseDetails
              )}
            </Box>
            {(condensed || smallScreen) && (
              <Box
                mb={0}
                mt={3}
                display="flex"
                flexDirection="column"
                alignItems="flex-start"
                justifyContent="space-between"
              >
                {flags}
                <Box
                  mt={4}
                  width={{
                    xs: "100%",
                    sm: buttonsVariant == "wide" ? 226 : 160
                  }}
                >
                  {buttons}
                </Box>
              </Box>
            )}
            {activitiesSummary}
          </Box>
          {!condensed && !smallScreen && (
            <Box
              mb={-1}
              mt={2}
              width={{ xs: "100%", sm: buttonsVariant == "wide" ? 226 : 160 }}
            >
              {buttons}
            </Box>
          )}
        </Box>
      </Box>
      {expansionPanel}
    </Paper>
  );
};

export default CourseCardListType;

CourseCardListType.propTypes = {
  condensed: PropTypes.bool,
  buttons: PropTypes.node,
  expansionPanel: PropTypes.node,
  imageSrc: PropTypes.string,
  hasNote: PropTypes.bool,
  activitiesSummary: PropTypes.node,
  reattemptMessage: PropTypes.node,
  courseType: PropTypes.string,
  typeName: PropTypes.string,
  dueDate: PropTypes.node,
  flags: PropTypes.node,
  title: PropTypes.string,
  courseDescription: PropTypes.string,
  buttonsVariant: PropTypes.oneOf(["wide", "narrow"])
};

CourseCardListType.defaultProps = {
  buttonsVariant: "wide"
};
